/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { LogoWrapper, Typography2, VisuallyHidden } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/composable-page-header';

type Props = {
  isCurrent: boolean;
  name: string;
  description?: string | null;
  logo?: React.ReactNode;
};

export const ORG_SWITCHER_LOGO_WIDTH = 36;

const styles = {
  organizationCard: css`
    display: flex;
    gap: var(--cds-spacing-100);
    align-items: center;
    justify-content: space-between;
    border-radius: var(--cds-border-radius-50);
    background-color: 'transparent';

    :hover {
      text-decoration: none;
      background-color: var(--cds-color-grey-25);
    }

    :active {
      text-decoration: none;
    }

    :focus {
      outline: 5px auto -webkit-focus-ring-color;
      outline-offset: -2px;
      text-decoration: none;
    }
  `,
  organizationCardCurrent: css`
    background-color: var(--cds-color-grey-25);
  `,
  org: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    flex-shrink: 1;
    min-width: 0;
  `,
  orgName: css`
    min-width: 0;
  `,
  logo: css`
    &.cds-LogoWrapper-base {
      /* Override the default styles for the logo wrapper */
      width: ${ORG_SWITCHER_LOGO_WIDTH}px;
      display: flex;
    }

    img {
      border-radius: var(--cds-border-radius-50);
    }

    text-align: center;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
  `,
  checkIcon: css`
    justify-self: end;
    flex-shrink: 0;
  `,
  oneLine: css`
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  `,
};

const OrganizationCard = ({ isCurrent, name, description, logo }: Props) => {
  return (
    <div css={[styles.organizationCard, isCurrent && styles.organizationCardCurrent]} data-testid="organization-card">
      <div css={styles.org}>
        {logo && (
          <LogoWrapper paddingVariant="none" css={styles.logo}>
            {logo}
          </LogoWrapper>
        )}
        <div css={styles.orgName}>
          <Typography2 variant="bodyPrimary" component="div" css={styles.oneLine}>
            {name}
          </Typography2>
          {description && (
            <Typography2 component="p" variant="bodySecondary" css={styles.oneLine} color="supportText">
              {description}
            </Typography2>
          )}
        </div>
      </div>
      {isCurrent && (
        <React.Fragment>
          <CheckIcon size="medium" color="interactive" css={styles.checkIcon} />
          <VisuallyHidden>{_t('Selected')}</VisuallyHidden>
        </React.Fragment>
      )}
    </div>
  );
};

export default OrganizationCard;
